<template>
  <div
    :class="
      bubble.sourceType === MessageSourceType.USER
        ? 'flex flex-row-reverse items-start mb-5 w-full pr-0 pl-12'
        : 'flex items-start mb-5 w-full pl-0 pr-12'
    "
  >
    <img
      :src="bubble.avatarUrl"
      alt=""
      class="w-9 h-9 rounded-full border border-gray-300"
      :class="
        bubble.sourceType === MessageSourceType.USER
          ? 'ml-2 bg-white mt-5'
          : 'mr-2 bg-white mt-5'
      "
    />
    <div
      :class="
        bubble.sourceType === MessageSourceType.USER
          ? 'flex flex-col items-end max-w-[70%]'
          : 'flex flex-col items-start max-w-[70%]'
      "
    >
      <div class="text-xs text-gray-400 mb-1">
        {{ kFormatTime(Number(bubble.timestamp)) }}
      </div>
      <div
        :class="
          bubble.sourceType === MessageSourceType.USER
            ? 'flex flex-col items-end rounded-lg bg-green-100 text-black px-4 py-2 text-sm break-words min-w-[40px] mb-1'
            : 'flex flex-col items-start rounded-lg bg-gray-100 text-black px-4 py-2 text-sm break-words min-w-[40px] mb-1'
        "
        class="gap-4"
      >
        <div v-for="(msg, idx) in bubble.getMessages()" :key="'msg-' + idx">
          <!-- 文件区（所有文件都交给 ItemFile 渲染，包括图片） -->
          <div
            v-if="getAllFiles(msg).length > 0"
            :class="
              bubble.sourceType === MessageSourceType.USER
                ? 'flex flex-row justify-end mb-1'
                : 'flex flex-row justify-start mb-1'
            "
          >
            <ItemFile
              v-for="(fileModel, index) in getAllFiles(msg)"
              :item="fileModel"
              :index="index"
              class="cursor-pointer"
              @click="handleDownloadFile"
              :key="'item-' + idx + '-' + index"
            />
          </div>
          <!-- 内容区：AI气泡支持打字机效果，用户气泡只显示最后一条 -->
          <div
            v-if="msg.getTextMessage ? msg.getTextMessage() : _extractText(msg)"
            :key="'msg-content-' + idx"
          >
            <MarkdownRenderer :content="msg.content" />
            <MessageFormWidget
              v-if="bubble.sourceType !== MessageSourceType.USER"
              :value="msg"
              :tools="msg.tool_interactions"
            />
          </div>
        </div>
        <img
          v-if="bubble.hasError"
          class="w-4 h-4 mr-2"
          src="@/assets/ChatGPT/error.png"
          alt=""
        />
      </div>
    </div>
  </div>
</template>

<script>
import { formatTime } from "@/utils/timeUtil";
import MarkdownRenderer from "@/components/markdown/MarkdownRenderer.vue";
import ItemFile from "./ItemFile.vue";
import MessageFormWidget from "./MessageFormWidget/MessageFormWidget.vue";
import { MessageSourceType } from "./message-model";

export default {
  name: "MessageBubble",
  components: {
    MarkdownRenderer,
    ItemFile,
    MessageFormWidget,
  },
  props: {
    bubble: {
      type: Object,
      required: true,
      validator(value) {
        return (
          "sourceType" in value &&
          "timestamp" in value &&
          Array.isArray(value.messageList)
        );
      },
    },
  },
  data() {
    return {
      MessageSourceType,
    };
  },
  methods: {
    kFormatTime: formatTime,
    getAllFiles(msgObj) {
      let files = [];
      if (
        msgObj.files &&
        Array.isArray(msgObj.files) &&
        msgObj.files.length > 0
      ) {
        files = msgObj.files;
      } else if (Array.isArray(msgObj.message) && msgObj.message.length > 0) {
        files = [];
        for (const f of msgObj.message) {
          let type = "text/plain";
          if (
            f.content &&
            typeof f.content === "string" &&
            f.content.startsWith("data:")
          ) {
            const mimeMatch = f.content.match(/^data:([^;]+);base64,/);
            if (mimeMatch && mimeMatch[1]) {
              type = mimeMatch[1];
            }
          } else if (f.type) {
            type = f.type;
          }
          files.push({
            name: f.filename || f.name,
            type,
            url: f.url || "",
            content: f.content,
          });
        }
      } else if (
        msgObj.message &&
        Array.isArray(msgObj.message.files) &&
        msgObj.message.files.length > 0
      ) {
        files = msgObj.message.files;
      } else if (
        this.bubble &&
        Array.isArray(this.bubble.files) &&
        this.bubble.files.length > 0
      ) {
        files = this.bubble.files;
      }
      return files.map((f) => {
        if (f.url) return f;
        if (f.file && typeof f.file === "object") {
          return {
            ...f,
            url: f.url || URL.createObjectURL(f.file),
          };
        }
        return f;
      });
    },
    handleDownloadFile(file) {
      if (
        file.content &&
        typeof file.content === "string" &&
        !file.content.startsWith("data:")
      ) {
        const blob = new Blob([file.content], { type: "text/plain" });
        file = new File([blob], file.filename || file.name || "download.txt", {
          type: "text/plain",
        });
      } else if (
        file.content &&
        typeof file.content === "string" &&
        file.content.startsWith("data:")
      ) {
        const arr = file.content.split(",");
        const mimeMatch = arr[0].match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        file = new File(
          [u8arr],
          file.filename ||
            file.name ||
            "download" +
              (mime.split("/")[1] ? "." + mime.split("/")[1] : ".txt"),
          { type: mime }
        );
      }
      const link = document.createElement("a");
      if (file instanceof File) {
        link.href = URL.createObjectURL(file);
      } else {
        link.href = file.url;
      }
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    },
    _extractText(msg) {
      if (msg.message && typeof msg.message.content === "string") {
        return msg.message.content;
      }
      if (typeof msg.content === "string") {
        return msg.content;
      }
      if (Array.isArray(msg.content)) {
        return msg.content.map((c) => c.content || "").join("");
      }
      return "";
    },
  },
};
</script>
